<template>
    <div class="root-menu-container"
         @mouseenter="handleMouseEnter"
         @mouseleave="handleMouseLeave">
        <RootMenuTitle :title="title"
                       :isLink="isLink"
                       :url="url"
                       :urlTarget="urlTarget"
                       :showArrow="!menuStore.isDesktop && hasChildren"
                       :arrowDirection="arrowDirection"
                       :isCurrent="isCurrent"
                       :badge="badge"
                       @mouseenter="handleMouseTitleEnter"
                       @click="isLink ? null : handleClick()" />
        <SlideTransition :disabled="menuStore.isDesktop || isChildBoxType"
                         :childCount="numberOfLvl2Submenus"
                         :childHeight="menuStore.menuItemHeight">
            <NavigationMenu v-if="currentMenu.isOpen && hasChildren"
                            :menuLevel="MENU_STARTING_LEVEL"
                            :parentsTitle="title"
                            :menu="currentMenu"
                            @reset-sibling="menuStore.resetChildren(currentMenu)"
                            @close-menu="currentMenu.isOpen = false" />
        </SlideTransition>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { useMenuStore } from '@/js/store/menu/MenuStore';
    import { DirectionsEnum } from '@/js/enums/directions';
    import NavigationMenu from './NavigationMenu.vue';
    import SlideTransition from '../../../transitions/SlideTransition.vue';
    import { HideOptionEnum, MenuTypeEnum, NavigationActionsEnum } from '../enums/menuEnums';
    import RootMenuTitle from '../partials/RootMenuTitle.vue';
    import { useSubscribeModalStore } from '@/js/store/subscribe/SubscribeModalStore';
    import {
        TMenu, INavigationMenu, INavigationItem,
    } from '../types/menuTypes';

    const props = defineProps<{
        menu: TMenu,
    }>();

    const menuStore = useMenuStore();
    const subscribeModalStore = useSubscribeModalStore();

    const MENU_STARTING_LEVEL = 2;

    const isNavigationMenu = (menu: TMenu): menu is INavigationMenu => 'is_current' in menu && 'title' in menu;
    const isNavigationItemMenu = (menu: TMenu): menu is INavigationItem => 'child_box_type' in menu;
    const isHeading = (menu) => menu?.type === MenuTypeEnum.HEADING;

    const currentMenu = computed(() => props.menu);

    const action = computed(() => (isNavigationMenu(currentMenu.value) ? currentMenu.value.action : ''));
    const title = computed(() => (isNavigationMenu(currentMenu.value) ? currentMenu.value.title : ''));
    const url = computed(() => (isNavigationMenu(currentMenu.value) ? currentMenu.value.url || '' : ''));
    const urlTarget = computed(() => (isNavigationMenu(currentMenu.value) ? currentMenu.value.url_target || '_self' : '_self'));
    const badge = computed(() => (isNavigationMenu(currentMenu.value) ? currentMenu.value.badge || undefined : undefined));
    const isCurrent = computed(() => (isNavigationMenu(currentMenu.value) ? currentMenu.value.is_current : false));
    const hasChildren = computed(() => ('children' in currentMenu.value));
    const isLink = computed(() => (menuStore.isDesktop ? !!url.value : (!!url.value && !hasChildren.value)));
    const isChildBoxType = computed(() => (isNavigationItemMenu(currentMenu.value) ? !!currentMenu.value.child_box_type || false : false));

    const isMouseEnterEventActive = ref(false);
    const numberOfLvl2Submenus = ref(0);

    const arrowDirection = computed(() => {
        if (hasChildren.value && isChildBoxType.value) {
            return DirectionsEnum.RIGHT;
        }

        if (currentMenu.value.isOpen) {
            return DirectionsEnum.UP;
        }

        return DirectionsEnum.DOWN;
    });

    const calculateVisibleSubmenus = () => {
        if (!isNavigationMenu(currentMenu.value) || !currentMenu.value.children) {
            return 0;
        }

        const hideCondition = menuStore.isDesktop ? HideOptionEnum.DESKTOP : HideOptionEnum.MOBILE;

        return currentMenu.value.children.filter((menu) => {
            const isHeadingOnMobile = !menuStore.isDesktop && isHeading(menu);
            const isElementHidden = menu.hide === hideCondition;

            return !(isHeadingOnMobile || isElementHidden);
        }).length;
    };

    numberOfLvl2Submenus.value = calculateVisibleSubmenus();

    const handleMouseTitleEnter = () => {
        menuStore.openDefaultSubmenu(currentMenu.value);
    };

    const handleMouseEnter = () => {
        if (menuStore.isDesktop && !isMouseEnterEventActive.value) {
            isMouseEnterEventActive.value = true;
            menuStore.openDefaultSubmenu(currentMenu.value);
            currentMenu.value.isOpen = true;
        }
    };

    const handleMouseLeave = () => {
        if (menuStore.isDesktop) {
            currentMenu.value.isOpen = false;
            menuStore.resetMenu();
            isMouseEnterEventActive.value = false;
        }
    };

    const handleClick = () => {
        if (!menuStore.isDesktop) {
            currentMenu.value.isOpen = !currentMenu.value.isOpen;
        }

        if (action.value) {
            subscribeModalStore.setSubscribeModal(true, action.value === NavigationActionsEnum.NEWSLETTER_MODAL);
        }
    };

    onMounted(() => {
        numberOfLvl2Submenus.value = calculateVisibleSubmenus();
    });
</script>

<style scoped lang="scss">
.root-menu-container {
    position: relative;
    display: flex;
    align-items: center;
}

.mobile-nav {
    .root-menu-container {
        flex-direction: column;
    }
}
</style>
